/**
 * ─────────────────────────────────────────────
 * src/lib/taskHours.js
 * ─────────────────────────────────────────────
 * Shared "estimated hours" cap used everywhere a task's start/end date and
 * estimated hours are entered (Create Project's task step, Kanban's Add/Edit
 * Task modals, Projects' task creation) — max 8 hours per working day
 * spanned by the task, inclusive of both the start and end date. Sundays
 * are non-working days and are excluded from the count.
 *
 * Example: start = 16th, end = 17th, neither a Sunday → 2 working days →
 * 16 hours max. If one of those two days falls on a Sunday, only 1 working
 * day counts → 8 hours max.
 */

const MAX_HOURS_PER_DAY = 8;

// Number of working days (Mon–Sat) between two dates, inclusive of both
// ends, with Sundays excluded. Falls back to 1 day if the dates are
// invalid, missing, or the whole range is Sundays only.
export const getInclusiveDayCount = (startDate, endDate) => {
  if (!startDate || !endDate) return 1;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;

  // Compare by calendar day only, ignoring time-of-day.
  const startDay = Date.UTC(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  );
  const endDay = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());

  const totalDays = Math.round((endDay - startDay) / (1000 * 60 * 60 * 24)) + 1;
  if (totalDays <= 0) return 1;

  let workingDays = 0;
  for (let i = 0; i < totalDays; i++) {
    const day = new Date(startDay + i * 24 * 60 * 60 * 1000);
    if (day.getUTCDay() !== 0) workingDays += 1; // 0 = Sunday
  }

  return workingDays > 0 ? workingDays : 1;
};

export const getMaxEstimatedHours = (startDate, endDate) =>
  getInclusiveDayCount(startDate, endDate) * MAX_HOURS_PER_DAY;
